import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { ensureArray } from "shared/lib/arrayUtils"
import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import "./styles/_formatted-price-groups.scss"

export interface FormattedPriceGroupsProps {
  groups?: string | string[] | null
  isLoading?: boolean
  isPriceServiceAvailable?: boolean
  unavailabilityMessage?: string
  className?: string
  "data-testid"?: string
}

export const FormattedPriceGroups = React.memo(
  React.forwardRef<HTMLSpanElement, FormattedPriceGroupsProps>(
    (
      {
        groups,
        isLoading = false,
        isPriceServiceAvailable = true,
        unavailabilityMessage,
        className,
        ...rest
      },
      ref
    ) => {
      const { t } = useTranslation()

      const formattedGroups = useMemo(() => {
        if (!groups || groups.length === 0) {
          return t("Price.NoGroup") as string
        }
        return ensureArray(groups).join(", ")
      }, [groups, t])

      if (isLoading) {
        return (
          <span
            {...rest}
            ref={ref}
            className={classNames(
              "formatted-price-groups",
              "formatted-price-groups--loading",
              className
            )}
          >
            <Skeleton />
          </span>
        )
      }

      if (!isPriceServiceAvailable) {
        return (
          <span
            {...rest}
            ref={ref}
            className={classNames(
              "formatted-price-groups",
              "formatted-price-groups--unavailable",
              className
            )}
          >
            <Icon size={IconSize.SMALL} name={IconName.CLOSE_CIRCLE} />
            {unavailabilityMessage || (t("Price.ServiceUnavailable") as string)}
          </span>
        )
      }

      return (
        <span
          {...rest}
          ref={ref}
          className={classNames("formatted-price-groups", className)}
        >
          {formattedGroups}
        </span>
      )
    }
  )
)

FormattedPriceGroups.displayName = "FormattedPriceGroups"
